'use client';

import { ClipSlide } from './clip-slide';
import { useClipSwiper } from './use-clip-swiper';

export interface IClipSwiperItem {
  id: number;
  verticalUrl: string;
  transcript: string | null;
  parentTitle: string | null;
  collectionTitle: string | null;
}

interface IClipCarouselProps {
  clips: IClipSwiperItem[];
  language: string;
  isMuted: boolean;
  isPaused: boolean;
  onTap: () => void;
  onClipChange: () => void;
}

const VIEWPORT_CLASS =
  'relative size-full touch-none select-none overflow-hidden rounded-[inherit]';

function slideKey(position: number, total: number, clip: IClipSwiperItem): string {
  if (position === 0) {
    return 'clone-head';
  }
  if (position === total - 1) {
    return 'clone-tail';
  }
  return `clip-${clip.id}`;
}

export function ClipCarousel({
  clips,
  language,
  isMuted,
  isPaused,
  onTap,
  onClipChange,
}: IClipCarouselProps): React.JSX.Element | null {
  const { index, dragPx, withTransition, total, activeRealIndex, viewportRef, step, handlers } =
    useClipSwiper(clips.length, onTap, onClipChange);

  const firstClip = clips[0];
  const lastClip = clips.at(-1);
  if (!firstClip || !lastClip) {
    return null;
  }
  const slides = [lastClip, ...clips, firstClip];

  return (
    <div
      ref={viewportRef}
      className={VIEWPORT_CLASS}
      onPointerDown={handlers.onPointerDown}
      onPointerMove={handlers.onPointerMove}
      onPointerUp={handlers.onPointerUp}
      onPointerCancel={handlers.onPointerUp}
      onClickCapture={handlers.onClickCapture}
    >
      <div
        className="absolute inset-x-0 top-0 w-full"
        style={{
          height: `${total * 100}%`,
          transform: `translateY(calc(${(-index * 100) / total}% + ${dragPx}px))`,
          transition: withTransition ? 'transform 440ms cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
        }}
        onTransitionEnd={handlers.onTransitionEnd}
      >
        {slides.map((clip, position) => (
          <div
            key={slideKey(position, total, clip)}
            className="relative w-full"
            style={{ height: `${100 / total}%` }}
          >
            <ClipSlide
              src={clip.verticalUrl}
              transcript={clip.transcript}
              language={language}
              parentTitle={clip.parentTitle}
              collectionTitle={clip.collectionTitle}
              isActive={position === index}
              isMuted={isMuted}
              isPaused={isPaused}
              onTap={onTap}
              onEnded={() => step(1)}
            />
          </div>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-y-0 right-2 z-20 flex flex-col items-center justify-center gap-1.5">
        {clips.map((clip, dot) => (
          <span
            key={clip.id}
            className={`w-1.5 rounded-full transition-all duration-300 ${
              dot === activeRealIndex ? 'h-4 bg-white' : 'h-1.5 bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
